/**
 * DataList cell renderer component.
 *
 * The foundational visual component that renders a single cell in the data
 * list table. Handles all column types (text, badge, boolean, link, icon,
 * custom), applies text alignment/transform, conditional formatting,
 * conditional icons, and cell tooltips.
 *
 * Delegates value formatting to `cellRenderer.renderCellValue()` and
 * condition evaluation to `conditionalEvaluator`.
 */

import * as React from 'react';
import * as lucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Table } from '../../../base/data/Table/Table';
import { Badge } from '../../../base/display/Badge/Badge';
import type { BadgeVariant } from '../../../base/display/Badge/Badge';
import { Icon } from '../../../base/display/Icon/Icon';
import type { IconColor } from '../../../base/display/Icon/Icon';
import { Tooltip } from '../../../base/display/Tooltip/Tooltip';

import { renderCellValue, safeString } from '../utils/cellRenderer';
import type { CellRenderResult } from '../utils/cellRenderer';
import { evaluateCellRules, evaluateConditionalIcons } from '../utils/conditionalEvaluator';
import type { CellStyles, IconResult } from '../utils/conditionalEvaluator';
import { dataListWorkflowRegistry } from '../workflow/registry';
import { ALIGN_CLASSES, TEXT_TRANSFORM_CLASSES } from '../constants';
import type { ColumnConfig } from '../types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DataListCellProps {
  /** Column configuration (includes the column key). */
  column: { key: string } & ColumnConfig;
  /** Raw cell value from the row data. */
  value: unknown;
  /** Full row data. */
  row: Record<string, unknown>;
  /** Additional CSS class applied to the cell. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Icon name resolver
// ---------------------------------------------------------------------------

/**
 * Resolves a kebab-case or PascalCase icon name to a Lucide icon component.
 *
 * Converts names like `"arrow-up"` to `"ArrowUp"` and looks the component up
 * in the `lucide-react` module namespace.
 */
function resolveLucideIcon(name: string): LucideIcon | undefined {
  // Convert kebab-case to PascalCase: "arrow-up" -> "ArrowUp"
  const pascalName = name.replaceAll(/(^|-)(\w)/g, (_match, _sep, char: string) =>
    char.toUpperCase(),
  );
  return (lucideIcons as unknown as Record<string, LucideIcon>)[pascalName];
}

// ---------------------------------------------------------------------------
// Internal sub-renderers
// ---------------------------------------------------------------------------

function renderTextContent(result: CellRenderResult): React.ReactNode {
  return <span>{result.text}</span>;
}

function renderBadgeContent(result: CellRenderResult): React.ReactNode {
  /* v8 ignore start */
  const variant = (result.badgeVariant ?? 'default') as BadgeVariant;
  /* v8 ignore stop */
  return <Badge variant={variant}>{result.text}</Badge>;
}

function renderBooleanContent(result: CellRenderResult): React.ReactNode {
  /* v8 ignore start */
  const iconComponent = result.icon ? resolveLucideIcon(result.icon) : undefined;
  /* v8 ignore stop */
  if (!iconComponent) {
    return null;
  }
  /* v8 ignore start */
  const color = (result.iconColor ?? 'inherit') as IconColor;
  /* v8 ignore stop */
  return <Icon icon={iconComponent} color={color} size="sm" aria-label={result.icon} />;
}

function renderLinkContent(result: CellRenderResult): React.ReactNode {
  return (
    <a
      href={result.href}
      target={result.external ? '_blank' : undefined}
      rel={result.external ? 'noopener noreferrer' : undefined}
      className="text-primary-700 underline hover:text-primary-800"
    >
      {result.text}
    </a>
  );
}

function renderIconContent(result: CellRenderResult): React.ReactNode {
  /* v8 ignore start */
  const iconComponent = result.icon ? resolveLucideIcon(result.icon) : undefined;
  /* v8 ignore stop */
  if (!iconComponent) {
    return null;
  }
  return <Icon icon={iconComponent} size="sm" aria-label={result.icon} />;
}

function renderCustomContent(
  result: CellRenderResult,
  value: unknown,
  row: Record<string, unknown>,
  column: ColumnConfig,
): React.ReactNode {
  if (!result.rendererKey) {
    return <span>{result.text}</span>;
  }
  const renderer = dataListWorkflowRegistry.getCellRenderer(result.rendererKey);
  if (!renderer) {
    return <span>{result.text}</span>;
  }
  return renderer(value, row, column);
}

// ---------------------------------------------------------------------------
// Render result dispatcher
// ---------------------------------------------------------------------------

/**
 * Renders a cell's content from its `CellRenderResult` by column type — text,
 * badge, boolean, link, icon, and `custom` (the renderer registered in
 * `dataListWorkflowRegistry` under `typeOptions.renderer`, falling back to the
 * raw text when none is registered).
 *
 * Shared with `DataListMobileCard` so the desktop and mobile layouts resolve a
 * cell's renderer identically.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function renderResultContent(
  result: CellRenderResult,
  value: unknown,
  row: Record<string, unknown>,
  column: ColumnConfig,
): React.ReactNode {
  /* v8 ignore start */
  switch (result.type) {
    /* v8 ignore stop */
    case 'text':
      return renderTextContent(result);
    case 'badge':
      return renderBadgeContent(result);
    case 'boolean':
      return renderBooleanContent(result);
    case 'link':
      return renderLinkContent(result);
    case 'icon':
      return renderIconContent(result);
    case 'custom':
      return renderCustomContent(result, value, row, column);
    /* v8 ignore start */
    default:
      return <span>{result.text}</span>;
    /* v8 ignore stop */
  }
}

// ---------------------------------------------------------------------------
// Conditional icon rendering
// ---------------------------------------------------------------------------

function renderConditionalIcon(iconResult: IconResult): React.ReactNode {
  const iconComponent = resolveLucideIcon(iconResult.icon);
  if (!iconComponent) {
    return null;
  }
  /* v8 ignore start */
  const color = (iconResult.color ?? 'inherit') as IconColor;
  /* v8 ignore stop */
  return (
    <Icon
      icon={iconComponent}
      color={color}
      size="sm"
      aria-label={iconResult.icon}
    />
  );
}

// ---------------------------------------------------------------------------
// Build inline styles from conditional formatting
// ---------------------------------------------------------------------------

function buildConditionalStyle(cellStyles: CellStyles): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (cellStyles.backgroundColor) {
    style.backgroundColor = cellStyles.backgroundColor;
  }
  if (cellStyles.textColor) {
    style.color = cellStyles.textColor;
  }
  if (cellStyles.fontWeight) {
    style.fontWeight = cellStyles.fontWeight;
  }
  return style;
}

// ---------------------------------------------------------------------------
// Icon attachment helper
// ---------------------------------------------------------------------------

function attachConditionalIcon(
  content: React.ReactNode,
  iconResult: IconResult | null,
): React.ReactNode {
  if (!iconResult) return content;

  const iconElement = renderConditionalIcon(iconResult);
  if (iconResult.position === 'replace') {
    return iconElement;
  }
  if (iconResult.position === 'after') {
    return (
      <span className="inline-flex items-center gap-1">
        {content}
        {iconElement}
      </span>
    );
  }
  // 'before' (default)
  return (
    <span className="inline-flex items-center gap-1">
      {iconElement}
      {content}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tooltip wrapping helper
// ---------------------------------------------------------------------------

function wrapWithTooltip(
  content: React.ReactNode,
  column: { key: string } & ColumnConfig,
  row: Record<string, unknown>,
): React.ReactNode {
  if (!column.cellTooltip) return content;

  let tooltipText: string | undefined;
  if (column.cellTooltip.text) {
    tooltipText = column.cellTooltip.text;
  } else if (column.cellTooltip.field) {
    const fieldValue = row[column.cellTooltip.field];
    tooltipText = fieldValue == null ? undefined : safeString(fieldValue);
  }

  if (!tooltipText) return content;

  return (
    <Tooltip content={tooltipText}>
      <span>{content}</span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// DataListCell Component
// ---------------------------------------------------------------------------

export function DataListCell({
  column,
  value,
  row,
  className,
}: Readonly<DataListCellProps>) {
  // 1. Render cell value
  const result = renderCellValue(value, column, row);

  // 2. Render content based on type
  let content = renderResultContent(result, value, row, column);

  // 3. Evaluate conditional icons
  const iconResult = evaluateConditionalIcons(
    value,
    row,
    column.conditionalIcon ?? [],
  );
  content = attachConditionalIcon(content, iconResult);

  // 4. Wrap with tooltip if configured
  content = wrapWithTooltip(content, column, row);

  // 5. Build CSS classes
  const alignClass = column.align ? ALIGN_CLASSES[column.align] : undefined;
  const transformClass = column.textTransform
    ? TEXT_TRANSFORM_CLASSES[column.textTransform]
    : undefined;

  // 6. Evaluate conditional formatting
  const cellStyles = evaluateCellRules(
    value,
    row,
    column.conditionalFormatting ?? [],
  );
  const conditionalStyle = buildConditionalStyle(cellStyles);

  // 7. Assemble final class name
  const combinedClassName = [
    alignClass,
    transformClass,
    cellStyles.className,
    column.className,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Table.Cell
      align={column.align}
      className={combinedClassName || undefined}
    >
      {Object.keys(conditionalStyle).length > 0 ? (
        <span style={conditionalStyle}>{content}</span>
      ) : (
        content
      )}
    </Table.Cell>
  );
}

DataListCell.displayName = 'DataListCell';
